import { useEffect, useState } from "react";
import { Button, Form, Modal, Nav, Spinner, Tab } from "react-bootstrap";
import { toast } from "react-toastify";
import { AdminService } from "Services/adminService";
import { ExamServices } from "Services/examServices";
import { renderMarkdown } from "view/private/Settings/markdownHelper";

interface IUpdateExamInstructionModal {
    examId: string;
    show: boolean;
    setShow: (show: boolean) => void;
    reloadData?: () => void;
}

export default function UpdateExamInstructionModal(
    props: IUpdateExamInstructionModal
) {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchInstruction = async () => {
        if (!props.examId) return;
        setLoading(true);
        try {
            const res = await ExamServices.getExamById(props.examId);
            if (res.status === 200 && res.data.data) {
                setContent(res.data.data.instructionText || "");
            }
        } catch (e) {
            toast.error("Failed to load instructions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (props.show) {
            fetchInstruction();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await AdminService.updateExamInstruction(props.examId, {
                instructionText: content,
            });
            if (res.status === 200) {
                toast.success("Instructions saved successfully");
                props.reloadData?.();
                props.setShow(false);
            }
        } catch (e) {
            toast.error("Failed to save instructions");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            show={props.show}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            onHide={() => props.setShow(false)}
        >
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Exam Instructions
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" /> Loading...
                    </div>
                ) : (
                    <Form>
                        <p className="text-muted small mb-3">
                            This content is shown to candidates on the instructions page after
                            login. Supports Markdown formatting.
                        </p>

                        <Tab.Container defaultActiveKey="write">
                            <Nav variant="tabs" className="mb-0">
                                <Nav.Item>
                                    <Nav.Link eventKey="write">Write</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="preview">Preview</Nav.Link>
                                </Nav.Item>
                            </Nav>
                            <Tab.Content>
                                <Tab.Pane eventKey="write" className="pt-2">
                                    <Form.Control
                                        as="textarea"
                                        rows={14}
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        placeholder={
                                            "Write instructions in Markdown...\n\n# Heading\n**Bold text**\n*Italic text*\n\nParagraphs separated by blank lines."
                                        }
                                        style={{ fontFamily: "monospace", fontSize: 13 }}
                                    />
                                </Tab.Pane>
                                <Tab.Pane eventKey="preview" className="pt-2">
                                    <div
                                        className="border rounded p-3"
                                        style={{
                                            minHeight: 200,
                                            fontSize: 14,
                                            lineHeight: 1.7,
                                            overflowWrap: "break-word",
                                            wordBreak: "break-word",
                                            whiteSpace: "pre-wrap",
                                        }}
                                        dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
                                    />
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>

                        <Form.Text className="text-muted mt-2 d-block">
                            Supported: # Headings, **bold**, *italic*, blank lines for
                            paragraphs, - lists
                        </Form.Text>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" onClick={handleSave} disabled={saving || loading}>
                    {saving ? (
                        <>
                            <Spinner animation="border" size="sm" /> Saving
                        </>
                    ) : (
                        <>Save Changes</>
                    )}
                </Button>
                <Button onClick={() => props.setShow(false)}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}
